// src/pages/RefundCancellation.jsx
import React from "react";
import LegalPageLayout from "./LegalPageLayout";
import LegalDocumentContent from "./LegalDocumentContent";

export default function RefundCancellationPage() {
  const refundCancellationText = `GWIN REFUND AND CANCELLATION POLICY
Effective date: 1 May 2026
Platform: Gwin
Operator: Marion Rich Pty Ltd, registration number 2022 / 577834 / 07, trading as “Gwin”
Jurisdiction: Republic of South Africa
Contact email: admin@gwin.co.za

1. Purpose of This Policy
This Refund and Cancellation Policy explains how cancellations, renewals, billing disputes, refunds, free trials, sponsored access, and subscription changes are handled on Gwin.
This Policy applies to users who purchase, subscribe to, or access paid features, premium content, cohort programmes, partner programmes, or any other paid Gwin product or service.
This Policy should be read together with Gwin’s:
Terms of Use;
Privacy Policy;
POPIA Consent Notice;
Cookie Policy;
any applicable partner, pilot, cohort, or programme terms.
By purchasing or subscribing to any paid Gwin product or service, you agree to this Policy.

2. Nature of Gwin’s Paid Services
Gwin may offer free and paid services.
Paid services may include:
premium lessons;
full curriculum access;
templates;
practical exercises;
scoring features;
dashboards;
readiness assessments;
certificates;
cohort programmes;
advisor-supported programmes;
sponsored or partner-enabled programmes;
subscription plans;
once-off digital products;
future funding-readiness tools.
Gwin’s paid services provide access to digital educational, behavioural, and readiness-support tools. They do not guarantee business improvement, funding approval, investment, revenue growth, tax outcomes, compliance outcomes, credit outcomes, or any other financial result.

3. Subscription Plans
Gwin may offer monthly, annual, or other recurring subscription plans.
The subscription plan, price, billing cycle, included features, renewal terms, and cancellation process will be shown before payment.
By subscribing, you authorise Gwin or its payment provider to charge the applicable subscription fee on a recurring basis until the subscription is cancelled.
You are responsible for ensuring that your billing details and payment method remain accurate and up to date.

4. Automatic Renewal
Unless stated otherwise, paid subscriptions renew automatically at the end of each billing period.
For example:
a monthly subscription renews each month;
an annual subscription renews each year;
a cohort-based subscription may renew according to the specific programme terms.
If you do not want your subscription to renew, you must cancel it before the renewal date.
Failure to use the Platform does not automatically cancel your subscription.

5. How to Cancel a Subscription
You may cancel your subscription by:
using the cancellation option in your account settings, if available; or
contacting Gwin support at admin@gwin.co.za; or
following the cancellation process provided by the applicable app store, payment provider, or partner platform, if you subscribed through a third party.
Your cancellation request must include:
your full name;
account email address;
subscription type;
reason for cancellation, if you choose to provide one;
proof of payment if requested.
Cancellation will only be effective once processed through the correct cancellation channel.

6. Effect of Cancellation
When you cancel your subscription:
future billing will stop after the cancellation is processed;
you may continue using paid features until the end of your current paid billing period, unless your account is suspended or terminated for breach;
you will not usually receive a refund for the unused portion of the current billing period;
your access may revert to the free version, if available;
premium features, paid templates, certificates, dashboards, score history, or reports may become unavailable or limited;
some data may be retained in accordance with Gwin’s Privacy Policy and applicable law.
Cancellation does not remove your obligation to pay fees that became due before cancellation.

7. Refund Policy for Monthly Subscriptions
Monthly subscription fees are generally non-refundable once the billing period has started.
This means that if you cancel during a paid month, you will usually retain access until the end of that paid month, but you will not receive a partial refund.
Gwin may consider a refund at its discretion if:
you were charged in error;
you were charged twice for the same billing period;
a technical error caused incorrect billing;
you cancelled but were charged again due to a system error;
Gwin was unable to provide the paid service due to a confirmed platform failure;
applicable law requires a refund.

8. Refund Policy for Annual Subscriptions
Annual subscriptions are generally non-refundable once the billing period has started, unless otherwise required by law or expressly stated in a specific offer.
If you cancel an annual subscription, you may continue using paid features until the end of the annual billing period, unless your account is suspended or terminated for breach.
Gwin may, at its sole discretion, consider a partial or full refund for annual subscriptions in exceptional circumstances, such as:
duplicate billing;
billing error;
confirmed technical failure caused by Gwin;
inability to access the paid service due to a fault on Gwin’s side;
other circumstances required by applicable law.

9. Refund Policy for Once-Off Digital Products
Gwin may sell once-off digital products, such as templates, guides, reports, toolkits, or downloadable resources.
Once-off digital products are generally non-refundable once accessed, downloaded, opened, used, or delivered, unless:
the product was not delivered;
the wrong product was supplied;
the file was defective and could not reasonably be used;
Gwin cannot remedy the defect within a reasonable time;
applicable law requires a refund.
No refund will be provided merely because you changed your mind after accessing or downloading the digital product.

10. Refund Policy for Cohorts, Programmes, and Partner Access
Gwin may provide access through cohorts, sponsored programmes, accelerator programmes, ESD programmes, advisor-led programmes, or partner programmes.
Refunds for these programmes depend on the applicable programme terms.
The following general rules apply unless specific programme terms say otherwise:
If you paid directly for a cohort or programme, cancellation before the start date may qualify for a refund or credit, depending on the notice period.
Cancellation after the programme has started may not qualify for a refund.
Failure to attend, participate, complete lessons, submit tasks, or use the Platform does not automatically qualify for a refund.
If your access was sponsored by a third party, any refund or cancellation issue may need to be handled with the sponsor or programme partner.
Gwin may suspend or remove programme access if you breach the Terms of Use.
Suggested programme refund structure:
cancellation 7 or more days before programme start: eligible for refund or credit, less reasonable administrative or payment processing costs;
cancellation less than 7 days before programme start: refund at Gwin’s discretion;
cancellation after programme start: no refund, unless required by law or agreed in writing.

11. Free Trials
Gwin may offer free trials from time to time.
The trial period, included features, conversion terms, and billing date will be disclosed when you sign up.
If a free trial automatically converts to a paid subscription, Gwin will disclose this before payment details are collected or before billing begins.
To avoid being charged, you must cancel before the trial ends.
Gwin may limit, withdraw, or amend free trials at any time.
Users may not abuse free trials by creating multiple accounts or using false information.

12. Promotional Offers, Discounts, and Coupons
Gwin may offer promotional prices, discounts, coupons, sponsored access, or special offers.
Promotional offers may be limited by:
time period;
user type;
programme;
partner;
region;
number of users;
subscription plan;
payment method.
Unless stated otherwise:
promotional offers apply only to the period specified;
after the promotional period, standard pricing may apply;
discounts cannot be exchanged for cash;
promotional fees are non-refundable unless required by law;
Gwin may withdraw or change promotions at any time.

13. Payment Failures
If your payment fails, Gwin or its payment provider may retry the payment.
If payment remains unsuccessful, Gwin may:
notify you;
suspend paid access;
downgrade your account;
cancel your subscription;
restrict access to premium features;
recover unpaid amounts where legally permitted.
You remain responsible for unpaid fees incurred before cancellation or suspension.

14. Billing Errors
If you believe you were charged incorrectly, contact Gwin at admin@gwin.co.za as soon as possible.
Your billing query should include:
account email address;
payment date;
amount charged;
proof of payment;
description of the issue.
If Gwin confirms a billing error, we may issue a refund, credit, or correction.
Billing disputes should ideally be raised within 30 days of the disputed charge.

15. No Refunds for Expected Outcomes
Gwin does not provide refunds because:
you did not receive funding;
a funder rejected your application;
your score did not improve;
you did not complete lessons;
you did not use the Platform;
your business did not improve;
your revenue did not increase;
your compliance position did not improve;
you misunderstood the purpose of the Platform where it was accurately described;
you expected financial, legal, tax, accounting, lending, or investment advice;
you expected a guaranteed commercial result.
Gwin provides educational, behavioural, and readiness-support tools. Outcomes depend on your own actions, business circumstances, third-party decisions, and external market factors.

16. No Refunds for Breach of Terms
Gwin may refuse refunds where your account is suspended, restricted, or terminated because you breached the Terms of Use.
Examples include:
sharing login details;
copying content;
scraping content or data;
reverse engineering the Platform;
submitting false information;
gaming scores;
abusing free trials;
using Gwin for unlawful purposes;
reselling templates;
attempting to build a competing product using Gwin Content;
payment fraud;
abuse of staff, users, partners, or support channels.

17. Refunds for Technical Problems
If you experience a technical issue, contact Gwin support.
A technical issue will not automatically qualify for a refund if:
the issue is temporary;
the issue is caused by your device, browser, internet connection, or third-party system;
you did not follow reasonable troubleshooting steps;
the issue affects only a minor feature;
Gwin provides a reasonable workaround;
Gwin fixes the issue within a reasonable time.
Gwin may consider a refund, credit, or extension if a confirmed platform issue prevents reasonable access to the paid service for a significant period.

18. Chargebacks and Payment Disputes
If you initiate a chargeback or payment dispute without first contacting Gwin, we may suspend your account while the dispute is investigated.
If the chargeback is found to be invalid or abusive, Gwin may:
recover bank or payment processing fees;
terminate your subscription;
restrict future access;
refuse future refunds;
take legal action where appropriate.
This does not limit your legal rights to dispute unauthorised or unlawful charges.

19. Refund Method and Processing Time
Approved refunds will usually be made to the original payment method used.
Refund timing may depend on:
payment provider processing times;
bank processing times;
verification of the refund request;
whether additional information is required.
Gwin is not responsible for delays caused by banks, card networks, payment gateways, or third-party processors.

20. Payment Provider and App Store Purchases
If you purchased Gwin through a third-party payment platform, app store, marketplace, or partner platform, refunds and cancellations may be governed by that third party’s policies.
You may need to request cancellation or refund directly through that provider.
Gwin may not be able to process refunds directly for payments made through third-party platforms.

21. Changes to Pricing or Plans
Gwin may change its prices, plans, included features, or billing options from time to time.
For existing subscribers, Gwin will take reasonable steps to notify you of material price changes before they apply.
If you do not agree to the new price, you may cancel before the new price takes effect.
If you continue using the paid service after the new price applies, you will be deemed to have accepted the updated pricing.

22. Sponsored or Employer-Paid Access
If your access is paid for by a sponsor, employer, accountant, advisor, institution, funder, ESD programme, accelerator, or partner, your cancellation and refund rights may depend on the agreement between Gwin and that third party.
You may lose access if:
the sponsor stops paying;
the programme ends;
your eligibility changes;
the partner removes you from the programme;
you breach the Terms of Use;
the sponsored access period expires.
Refunds, if any, may be paid to the sponsoring party rather than to you.

23. Account Deletion vs Subscription Cancellation
Deleting your account is not always the same as cancelling your subscription.
Before deleting your account, you should cancel any active paid subscription through the correct cancellation channel.
If you request account deletion while a subscription is active, Gwin may assist with cancellation where possible, but you remain responsible for ensuring that recurring payments are stopped.
Some information may be retained after account deletion where required by law, payment records, tax obligations, dispute resolution, fraud prevention, or legitimate business purposes.

24. Consumer Protection and Legal Rights
Nothing in this Policy is intended to remove any rights you may have under applicable South African law, including the Consumer Protection Act, Electronic Communications and Transactions Act, POPIA, or any other law that applies.
If the law gives you a right to cancel or receive a refund that cannot be excluded, Gwin will comply with that law.

25. How to Request a Cancellation or Refund
To request a cancellation or refund, contact:
Email: admin@gwin.co.za
Subject line: Cancellation / Refund Request
Include:
full name;
account email address;
payment date;
amount paid;
subscription or product purchased;
proof of payment;
reason for request;
preferred outcome: cancellation, refund, credit, or correction.
Gwin may ask for additional information to verify your request.

26. Updates to This Policy
Gwin may update this Refund and Cancellation Policy from time to time.
The updated version will apply from the effective date shown at the top.
If changes materially affect existing subscribers, Gwin will take reasonable steps to notify users.

27. Contact Details
For refund, billing, cancellation, or payment queries, contact:
Marion Rich Pty Ltd t/a Gwin
Registration number: 2022 / 577834 / 07
Support email: admin@gwin.co.za
Address: 22 Percy Road, Ottery, Cape Town
Website: www.gwin.co.za`;

  return (
    <LegalPageLayout
      title="Refund and Cancellation"
      backTo="/contact"
      backLabel="Back"
    >
      <LegalDocumentContent documentText={refundCancellationText} />
    </LegalPageLayout>
  );
}